import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { Contractor, ContractorInsert } from "../../../shared/schema";
import { getQueryFn, apiRequest, queryClient } from "../lib/queryClient";
import { useToast } from './use-toast';
import { useLocation } from "wouter";

type AuthContextType = {
  user: Contractor | null;
  isLoading: boolean;
  error: Error | null;
  isAuthenticated: boolean;
  loginMutation: UseMutationResult<Contractor, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<Contractor, Error, RegisterData>;
  isSessionRecoveryActive: boolean;
  isLoggingOut: boolean;
  refreshSession: () => Promise<void>;
};

type LoginData = {
  email: string;
  password: string;
};

type RegisterData = {
  firstName: string;
  lastName: string;
  companyName: string;
  username: string;
  email: string;
  password: string;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [isSessionRecoveryActive, setIsSessionRecoveryActive] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [, setLocation] = useLocation();
  
  const {
    data: user,
    error,
    isLoading,
    refetch
  } = useQuery<Contractor | null>({
    queryKey: ["/api/user"],
    queryFn: async () => {
      // Don't fetch if we're in the process of logging out
      if (isLoggingOut) {
        return null;
      }
      
      // First, check localStorage as primary source to prevent unnecessary API calls
      const localUser = localStorage.getItem('user');
      if (localUser) {
        try {
          const parsedUser = JSON.parse(localUser);
          if (parsedUser && parsedUser.id && parsedUser.email) {
            console.log('🔍 useAuth - Using localStorage user:', parsedUser.email);
            // Still try to refresh from API, but use localStorage as fallback
          }
        } catch (e) {
          console.error('❌ Error parsing localStorage user:', e);
        }
      }
      
      try {
        // Check if we have an access token
        const accessToken = localStorage.getItem('accessToken');
        if (!accessToken) {
          // No token, use localStorage fallback
          if (localUser) {
            try {
              const parsedUser = JSON.parse(localUser);
              if (parsedUser && parsedUser.id && parsedUser.email) {
                console.log('🔍 useAuth - No token, using localStorage user:', parsedUser.email);
                return parsedUser as Contractor;
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
          throw new Error('No access token available');
        }
        
        const res = await fetch("/api/user", { 
          credentials: "include",
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
        if (res.ok) {
          const userData = await res.json();
          console.log('🔍 useAuth - API response:', userData);
          // Handle both old format (direct user) and new format ({ user: {...} })
          let user = userData.user || userData;
          
          // Ensure logoUrl is properly formatted (handle both snake_case and camelCase)
          if (user.logo_url && !user.logoUrl) {
            user.logoUrl = user.logo_url;
          }
          // Remove snake_case version if camelCase exists
          if (user.logoUrl) {
            delete user.logo_url;
          }
          
          console.log('🔍 useAuth - extracted user:', user);
          // Validate that we have a real user with required fields
          if (user && user.id && user.email) {
            console.log('🔍 useAuth - returning authenticated user:', user.email, 'plan:', user.plan, 'logoUrl:', user.logoUrl ? 'present' : 'not set');
            // CRITICAL: Always update localStorage with fresh API data to prevent plan switching
            localStorage.setItem('user', JSON.stringify(user));
            console.log('✅ Updated localStorage with fresh user data, plan:', user.plan);
            return user as Contractor;
          }
        } else if (res.status === 401) {
          // Token is invalid/expired - but don't clear immediately, try localStorage first
          console.log('🔐 Token invalid, checking localStorage fallback');
          
          // Try localStorage fallback before clearing
          if (localUser) {
            try {
              const parsedUser = JSON.parse(localUser);
              if (parsedUser && parsedUser.id && parsedUser.email) {
                console.log('🔍 Using localStorage user as fallback after 401:', parsedUser.email);
                // Don't clear - just return the fallback user
                return parsedUser as Contractor;
              }
            } catch (e) {
              console.error('❌ Error parsing localStorage user after 401:', e);
            }
          }
          
          // Only clear if no valid fallback
          console.log('🔐 No valid fallback, clearing auth data');
          queryClient.setQueryData(["/api/user"], null);
          localStorage.clear();
          sessionStorage.clear();
          throw new Error('Authentication expired');
        }
        
        // If API fails for other reasons, try localStorage as fallback
        if (localUser) {
          try {
            const parsedUser = JSON.parse(localUser);
            if (parsedUser && parsedUser.id && parsedUser.email) {
              console.log('🔍 Using localStorage user as fallback:', parsedUser.email);
              return parsedUser as Contractor;
            }
          } catch (e) {
            console.error('❌ Error parsing localStorage user:', e);
          }
        }
        
        // If no valid user, return null to force login
        return null;
      } catch (error) {
        // If API fails, try localStorage as fallback
        if (localUser) {
          try {
            const parsedUser = JSON.parse(localUser);
            if (parsedUser && parsedUser.id && parsedUser.email) {
              console.log('🔍 Using localStorage user as fallback after API error:', parsedUser.email);
              return parsedUser as Contractor;
            }
          } catch (e) {
            console.error('❌ Error parsing localStorage user after API error:', e);
          }
        }
        
        // If any error occurs and no localStorage fallback, return null
        // But don't throw - let React Query handle the error state
        return null;
      }
    },
    retry: false, // Don't retry - use localStorage fallback instead
    staleTime: 10 * 60 * 1000, // 10 minutes - increased to prevent unnecessary refetches
    enabled: !isLoggingOut, // Disable query when logging out
    refetchOnWindowFocus: false, // Don't refetch on window focus to prevent logout on refresh
    refetchOnMount: false, // Don't refetch on mount - use cached data
    refetchOnReconnect: false, // Don't refetch on reconnect - use cached data
    // Use localStorage as initial data to prevent flash of login page
    initialData: () => {
      try {
        const localUser = localStorage.getItem('user');
        if (localUser) {
          const parsed = JSON.parse(localUser);
          if (parsed && parsed.id && parsed.email) {
            return parsed as Contractor;
          }
        }
      } catch (e) {
        // Ignore
      }
      return undefined;
    },
  });
  
  // Almacenar información del usuario para posible recuperación
  useEffect(() => {
    if (user) {
      // Guardar email para posible reconexión
      localStorage.setItem('lastUserEmail', user.email);
    }
  }, [user]);

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      console.log('Attempting login with:', credentials.email);
      const res = await apiRequest("POST", "/api/login", credentials);
      const userData = await res.json();
      return userData;
    },
    onSuccess: (data: any) => {
      console.log('Login successful:', data.user.email);
      
      // Store JWT tokens
      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
      }
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }
      
      // Store user data in cache and localStorage
      queryClient.setQueryData(["/api/user"], data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('lastUserEmail', data.user.email);
      
      toast({
        title: "Login successful",
        description: `Welcome back, ${data.user.firstName || data.user.first_name || 'there'}!`,
      });
      
      // Redirect to dashboard after successful login
      setTimeout(() => {
        setLocation('/dashboard');
      }, 1000);
    },
    onError: (error: Error) => {
      console.error('Login failed:', error);
      toast({
        title: "Login failed",
        description: error.message || "Please check your credentials and try again.",
        variant: "destructive",
      });
    },
  });

  // Note: Removed refetch after login to prevent hitting disabled /api/user endpoint
  // The login success handler now manages user state directly

  const registerMutation = useMutation({
    mutationFn: async (credentials: RegisterData) => {
      console.log('Attempting registration with:', credentials.email);
      const res = await apiRequest("POST", "/api/register", credentials);
      return await res.json();
    },
    onSuccess: (response: any) => {
      console.log('Registration successful:', response);
      const user = response.user || response;
      
      // Store JWT tokens if provided
      if (response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken);
        console.log('✅ Access token stored');
      }
      if (response.refreshToken) {
        localStorage.setItem('refreshToken', response.refreshToken);
        console.log('✅ Refresh token stored');
      }
      
      // Set the new user data in cache and localStorage
      queryClient.setQueryData(["/api/user"], user);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('lastUserEmail', user.email);
      
      toast({
        title: "Registration successful",
        description: `Welcome to Remodra, ${user.firstName || user.first_name || 'there'}!`,
      });
      
      // Don't auto-redirect - let the register page handle the redirect
      // This allows the register page to redirect to billing for new users
    },
    onError: (error: Error) => {
      console.error('Registration failed:', error);
      
      // Try to parse the error message to extract user-friendly text
      let friendlyMessage = "Please check your information and try again.";
      
      try {
        // Check if error.message contains JSON
        if (error.message && error.message.includes('{')) {
          const jsonMatch = error.message.match(/\{.*\}/);
          if (jsonMatch) {
            const errorData = JSON.parse(jsonMatch[0]);
            friendlyMessage = errorData.message || friendlyMessage;
          }
        } else {
          friendlyMessage = error.message || friendlyMessage;
        }
      } catch (parseError) {
        // If parsing fails, use the original message or default
        friendlyMessage = error.message || friendlyMessage;
      }
      
      toast({
        title: "Registration failed",
        description: friendlyMessage,
        variant: "destructive",
      });
    },
  });

  // Note: Removed refetch after registration to prevent overriding new user data
  // The registration success handler now manages user state directly

  const logoutMutation = useMutation({
    mutationFn: async () => {
      console.log('🔐 Starting logout process...');
      setIsLoggingOut(true);
      
      // Invalidate ALL queries first to force refetch on navigation
      await queryClient.invalidateQueries();
      
      // Clear local data IMMEDIATELY
      queryClient.setQueryData(["/api/user"], null);
      queryClient.removeQueries(); // More thorough than clear()
      localStorage.clear();
      sessionStorage.clear();
      
      console.log('🔐 Local data cleared, redirecting...');
      
      // Force immediate redirect - use window.location for instant redirect
      window.location.href = '/simple-login';
      
      // Try API call in background (don't wait for it)
      try {
        await apiRequest("POST", "/api/logout");
        console.log('✅ Logout API successful');
      } catch (error) {
        console.warn('⚠️ Logout API failed, but user already redirected');
      }
    },
    onSuccess: () => {
      console.log('✅ Logout mutation successful');
      toast({
        title: "Logged out successfully",
        description: "You have been logged out.",
      });
    },
    onError: (error: Error) => {
      console.log('⚠️ Logout mutation error, but user already redirected');
      toast({
        title: "Logged out",
        description: "You have been logged out.",
      });
    },
  });

  // Función para intentar refrescar la sesión del usuario
  const refreshSession = async (): Promise<void> => {
    try {
      setIsSessionRecoveryActive(true);
      
      // Refrescar datos del usuario
      await refetch();
      
      // Verificar si la sesión se recuperó
      const currentUser = queryClient.getQueryData<Contractor | null>(["/api/user"]);
      
      if (currentUser) {
        // Removed session restored toast notification
      } else {
        // Removed session expired toast notification
      }
    } catch (error) {
      console.error("Error al refrescar la sesión:", error);
    } finally {
      setIsSessionRecoveryActive(false);
    }
  };
  
  // Monitorear si la sesión se pierde inesperadamente
  useEffect(() => {
    if (!isLoading && !user && localStorage.getItem('lastUserEmail')) {
      // Removed session expiration toast notification
    }
  }, [isLoading, user, toast]);

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isLoading,
        error: error as Error | null,
        isAuthenticated: !!user,
        loginMutation,
        logoutMutation,
        registerMutation,
        isSessionRecoveryActive,
        isLoggingOut,
        refreshSession
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
